import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ELanguage } from '@app/app.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { INews } from '@core/interfaces/news.interface';
import { AuthService } from '@core/services/auth.service';
import { TableStateService } from '@core/services/table-state.service';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AdminNewsDialogComponent } from '@pages/admin/admin-news/admin-news-dialog/admin-news-dialog.component';
import { IAdminNewsDialogData } from '@pages/admin/admin-news/admin-news-dialog/admin-news-dialog.interface';
import { BannerCardComponent } from '@shared/components/banner-card/banner-card.component';
import { BaseComponent } from '@shared/components/base.component';
import { IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { ICreateEntityButtonConfiguration } from '@shared/components/create-entity-button/create-entity-button.interface';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import { IDatatableConfiguration, TDatatableColumn } from '@shared/components/datatable/datatable.interface';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter } from 'rxjs';
import { AdminNewsStore } from './admin-news.store';
import { stripHtmlTags } from './admin-news.util';

type TNewsRow = INews & { id: string; startDate: Date | null; endDate: Date | null };

@Component({
  imports: [
    FormsModule,
    ContentContainerComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    TranslateModule,
    DotsSpinnerComponent,
    HasPermissionDirective,
    TextClampTooltipDirective,
    DatePipe,
    BannerCardComponent,
    NgxSkeletonLoaderModule,
    DatatableComponent,
    IconComponent,
  ],
  providers: [AdminNewsStore],
  selector: 'app-admin-news',
  styleUrl: './admin-news.component.scss',
  templateUrl: './admin-news.component.html',
})
export class AdminNewsComponent extends BaseComponent {
  public readonly stripHtmlTags = stripHtmlTags;

  public readonly languageOptions = Object.values(ELanguage);

  public readonly selectedLanguage = signal<ELanguage>(ELanguage.en);

  public readonly searchedOnce = signal(true);

  public readonly rowData = computed<TNewsRow[]>(() => {
    const news = this.store.news$() ?? [];

    return news
      .map((item) => ({
        ...item,
        endDate: item.end ? this.dateUtils.parseDateTimeFromApi(item.end) : null,
        id: item.id ?? `news-${item.start ?? Math.random()}`,
        startDate: item.start ? this.dateUtils.parseDateTimeFromApi(item.start) : null,
      }))
      .sort((a, b) => (b.endDate?.getTime() ?? 0) - (a.endDate?.getTime() ?? 0));
  });

  public readonly total = computed(() => this.rowData().length);
  public readonly hasResults = computed(() => this.rowData().length > 0);
  public readonly showEmptyState = computed(() => this.searchedOnce() && !this.store.loading$() && !this.hasResults());

  public readonly datatableColumns: TDatatableColumn<TNewsRow, string>[] = [
    {
      dataViewStrategy: (row) => row.startDate?.getTime() ?? 0,
      id: 'start',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'field.start',
    },
    {
      dataViewStrategy: (row) => row.endDate?.getTime() ?? 0,
      id: 'end',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'field.end',
    },
    {
      dataViewStrategy: (row) => row.priority ?? 0,
      id: 'priority',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'field.priority',
    },
    {
      dataViewStrategy: (row) => row.title ?? '',
      id: 'title',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'field.title',
    },
    {
      // Use the same plain-text representation as the rendered cell, otherwise sort/search operate
      // on the raw Quill HTML (e.g. searching "strong" would match any bold message).
      dataViewStrategy: (row) => stripHtmlTags(row.message ?? ''),
      id: 'message',
      isClientSideSearchable: true,
      isSortable: true,
      name: 'field.message',
    },
    {
      dataViewStrategy: () => '',
      id: 'actions',
      isSortable: false,
    },
  ];

  public datatableConfig!: IDatatableConfiguration<TNewsRow>;

  public readonly addButtonConfiguration: ICreateEntityButtonConfiguration = {
    labelKey: 'pages.admin.news.createButton',
    onClick: () => this.openDialog('create'),
    permissions: [EPermissions.newsCreate],
  };

  public readonly store = inject(AdminNewsStore);
  private readonly dialog = inject(MatDialog);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly tableStateService = inject(TableStateService);
  private readonly translate = inject(TranslateService);

  public readonly breadcrumbs: IBreadcrumb[] = [
    {
      label: this.translate.instant('pages.admin.news.navigationTitle'),
      link: '/dashboard',
    },
  ];

  constructor() {
    super();

    this.setDatatableConfig();
    this.onSearchClick();
  }

  private setDatatableConfig(): void {
    this.datatableConfig = {
      isClientSide: true,
      isLoaderEnabled: true,
      isPaginationEnabled: true,
      isRowClickEnabled: this.permissionService.hasPermission(this.EPermissions.newsUpdateAll),
      isSearchEnabled: true,
      pageSizeOptions: [5, 10, 25, 50, 100],
      showNoDataText: true,
      skipInitialRequest: true,
      storageKey: this.tableStateService.buildKey('admin-news', 'table'),
    };
  }

  public onSearchClick(): void {
    this.searchedOnce.set(true);
    this.store.fetchNewsAdmin(this.selectedLanguage());
  }

  public onLanguageChange(languageCode: ELanguage): void {
    this.selectedLanguage.set(languageCode);
  }

  public onRowClick(news: INews): void {
    if (!this.permissionService.hasPermission(this.EPermissions.newsUpdateAll)) {
      return;
    }

    this.openDialog('edit', news);
  }

  public onMenuButtonClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  public onEditClick(event: MouseEvent, news: INews): void {
    event.stopPropagation();
    this.openDialog('edit', news);
  }

  public onDeleteClick(event: MouseEvent, news: INews): void {
    event.stopPropagation();

    if (!news.id || this.store.deletingNewsId$()) {
      return;
    }

    const newsId = news.id;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.admin.news.deleteConfirmMessage', { title: news.title ?? '' }),
        title: this.translate.instant('pages.admin.news.deleteConfirmTitle'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.store.deleteNews({ id: newsId, languageCode: this.selectedLanguage() });
      });
  }

  private openDialog(mode: 'create' | 'edit', news: INews | null = null): void {
    const data: IAdminNewsDialogData = {
      defaultLanguageCode: this.selectedLanguage() ?? AuthService.currentDbUser?.language ?? ELanguage.en,
      mode,
      news,
    };

    const dialogRef = this.dialog.open<AdminNewsDialogComponent, IAdminNewsDialogData, INews | undefined>(
      AdminNewsDialogComponent,
      { data, disableClose: true, width: '727px' },
    );

    dialogRef.afterClosed().subscribe((result: INews | undefined) => {
      if (!result) {
        return;
      }

      if (mode === 'create') {
        this.store.addNews(result, this.selectedLanguage());
      } else {
        this.store.replaceNews(result, this.selectedLanguage());
      }
    });
  }
}
